import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { ImageUploadHelper } from "../src/image_upload_helper"
import { IngredientDetector } from "../src/ingredient_detector"

export default class extends Controller {
    static targets = ["searchInput", "searchResultsContainer", "searchForm", "clearButton", "mainContainer", "originalButtonsContainer", "imageUploadInput", "imageUploadButton", "dupesToggle", "dupesToggleBg", "dupesToggleIndicator", "searchIcon", "leftArrow", "cameraIcon", "filtersIcon", "backButton", "filtersButton"]
    static values = {
        toggle: Boolean,
        inputValue: String,
        inputFocused: { type: Boolean, default: false },
        directUploadUrl: { type: String, default: "/rails/active_storage/direct_uploads" },
        dupesActive: { type: Boolean, default: false }
    }

    initialize() {
        // Set placeholder from DOM element if it exists, otherwise fall back to native app detection
        this.originalPlaceholder = null; // Will be set in connect() when targets are available
        this.previousInputValue = ""; // Track previous input value to detect pastes
        this.boundHideResultsIfOutside = this.hideResultsIfOutside.bind(this)
        this.boundHandleFocus = this.handleFocus.bind(this)
        this.boundHandleBlur = this.handleBlur.bind(this)
        this.boundHandlePaste = this.handlePaste.bind(this)
        this.boundHandleClick = this.handleClick.bind(this)
        this.boundModifyDupeUrl = this.modifyDupeUrl.bind(this)
    }

    connect() {
        document.addEventListener("click", this.boundHideResultsIfOutside)
        this.searchInputTarget.addEventListener('focus', this.boundHandleFocus);
        this.searchInputTarget.addEventListener('blur', this.boundHandleBlur);
        this.searchInputTarget.addEventListener('paste', this.boundHandlePaste);
        this.searchInputTarget.addEventListener('click', this.boundHandleClick);

        // Set originalPlaceholder from DOM element, with fallback to native app detection
        if (this.hasSearchInputTarget && this.searchInputTarget.placeholder) {
            this.originalPlaceholder = this.searchInputTarget.placeholder;
        }
        document.addEventListener("turbo:before-visit", this.boundModifyDupeUrl);
    }

    disconnect() {
        document.removeEventListener("click", this.boundHideResultsIfOutside)
        if (this.hasSearchInputTarget) {
            this.searchInputTarget.removeEventListener('focus', this.boundHandleFocus);
            this.searchInputTarget.removeEventListener('blur', this.boundHandleBlur);
            this.searchInputTarget.removeEventListener('paste', this.boundHandlePaste);
            this.searchInputTarget.removeEventListener('click', this.boundHandleClick);
        }
        document.removeEventListener("turbo:before-visit", this.boundModifyDupeUrl);
    }

    modifyDupeUrl(event) {
        if (this.dupesActiveValue) {
            const originalUrl = event.detail.url;
            // Ensure we don't append /dupes multiple times or to non-product URLs
            if (!originalUrl.endsWith('/dupes') && originalUrl.includes('/products/')) {
                event.detail.url = originalUrl + '/dupes';
                event.preventDefault()
                Turbo.visit(event.detail.url)
            }
        }
    }

    toggleDupes() {
        this.dupesActiveValue = !this.dupesActiveValue;
    }

    dupesActiveValueChanged() {
        if (this.dupesActiveValue) {
            this.searchInputTarget.placeholder = "Search for a product to find dupes";
            if (this.hasImageUploadButtonTarget) {
                this.imageUploadButtonTarget.disabled = true;
                this.imageUploadButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
            }
            if (this.hasFiltersButtonTarget) {
                this.filtersButtonTarget.disabled = true;
                this.filtersButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
            }
            if (this.hasDupesToggleBgTarget) {
                this.dupesToggleBgTarget.classList.remove("bg-warm-gray-200");
                this.dupesToggleBgTarget.classList.add("bg-warm-gray-700"); // Active color from warm-gray palette
            }
            if (this.hasDupesToggleIndicatorTarget) {
                this.dupesToggleIndicatorTarget.classList.add("translate-x-5");
            }
            if (this.hasDupesToggleTarget) {
                this.dupesToggleTarget.classList.remove("text-warm-gray-500", "border-black/10");
                this.dupesToggleTarget.classList.add("text-warm-gray-900", "border-warm-gray-500");
            }

        } else {
            // Switch back to original placeholder
            if (this.originalPlaceholder) {
                this.searchInputTarget.placeholder = this.originalPlaceholder;
            }

            if (this.hasImageUploadButtonTarget) {
                this.imageUploadButtonTarget.disabled = false;
                this.imageUploadButtonTarget.classList.remove("opacity-50", "cursor-not-allowed");
            }
            if (this.hasFiltersButtonTarget) {
                this.filtersButtonTarget.disabled = false;
                this.filtersButtonTarget.classList.remove("opacity-50", "cursor-not-allowed");
            }
            if (this.hasDupesToggleBgTarget) {
                this.dupesToggleBgTarget.classList.add("bg-warm-gray-200");
                this.dupesToggleBgTarget.classList.remove("bg-warm-gray-700");
            }
            if (this.hasDupesToggleIndicatorTarget) {
                this.dupesToggleIndicatorTarget.classList.remove("translate-x-5");
            }
            if (this.hasDupesToggleTarget) {
                this.dupesToggleTarget.classList.add("text-warm-gray-500", "border-black/10");
                this.dupesToggleTarget.classList.remove("text-warm-gray-900", "border-warm-gray-500");
            }
        }
    }

    handleFocus() {
        this.inputFocusedValue = true;
        this.updateRingClass();
        // Hide back button when input is focused
        if (this.hasBackButtonTarget) {
            this.backButtonTarget.classList.add("hidden");
        }
    }

    handleBlur() {
        this.inputFocusedValue = false;
        this.updateRingClass();
        // Show back button when input loses focus, but only if search is not active
        if (this.hasBackButtonTarget && !this.toggleValue) {
            this.backButtonTarget.classList.remove("hidden");
        }
    }

    handleClick() {
        // Scroll to top on mobile when input is clicked, regardless of focus state
        this.scrollToTopOnMobile();
    }

    showResults() {
        this.toggleValue = true
    }

    hideResults() {
        this.toggleValue = false
    }

    hideResultsIfOutside(event) {
        if (!this.element.contains(event.target)) {
            this.hideResults()
        }
    }

    updateInput(event) {
        const currentValue = event.target.value;
        this.inputValueValue = currentValue;

        // Check if this looks like a paste operation (significant text addition)
        const textAdded = currentValue.length - this.previousInputValue.length;
        if (textAdded >= 20) { // Threshold for detecting paste operations
            this.checkForIngredientListPaste(currentValue);
        }

        // Update previous value for next comparison
        this.previousInputValue = currentValue;

        // Show search results when user starts typing
        if (currentValue.length > 0) {
            this.toggleValue = true
        }
    }

    clearInput(event) {
        event.stopPropagation()
        this.inputValueValue = ""
        this.searchInputTarget.value = ""
        this.searchInputTarget.focus()
    }

    submitSearch(event) {
        if ((event.type === 'keydown' && event.key === 'Enter') || event.type === 'submit') {
            event.preventDefault()
            const searchTerm = this.searchInputTarget.value
            const url = new URL('/products', window.location.origin)
            url.searchParams.set('product_filter[search]', searchTerm)
            Turbo.visit(url)
        }
    }

    updateRingClass() {
        if (this.toggleValue && this.inputFocusedValue) {
            this.mainContainerTarget.classList.add("!lg:ring-black/80")
        } else {
            this.mainContainerTarget.classList.remove("!lg:ring-black/80")
        }
    }

    toggleValueChanged() {
        this.updateRingClass();
        if (this.toggleValue) {
            this.searchResultsContainerTarget.classList.remove("hidden")
            if (this.hasOriginalButtonsContainerTarget) {
                this.originalButtonsContainerTarget.classList.add("hidden")
            }

            // On small screens: hide search icon, show left arrow
            if (this.hasSearchIconTarget) {
                this.searchIconTarget.classList.add("hidden")
            }
            if (this.hasLeftArrowTarget) {
                this.leftArrowTarget.classList.remove("hidden")
                const headerElement = document.getElementById("index-header-top");
                if (headerElement) {
                    headerElement.classList.add("hidden");
                }
            }
            // Hide camera and filters icons when search is active
            if (this.hasCameraIconTarget) {
                this.cameraIconTarget.classList.add("hidden")
            }
            this.filtersIconTargets.forEach(icon => {
                icon.classList.add("hidden")
            })
            // Hide back button when search is active
            if (this.hasBackButtonTarget) {
                this.backButtonTarget.classList.add("hidden")
            }
        } else {
            this.searchResultsContainerTarget.classList.add("hidden")
            if (this.hasOriginalButtonsContainerTarget) {
                this.originalButtonsContainerTarget.classList.remove("hidden")
            }

            // On small screens: show search icon, hide left arrow
            if (this.hasSearchIconTarget) {
                this.searchIconTarget.classList.remove("hidden")
            }
            if (this.hasLeftArrowTarget) {
                this.leftArrowTarget.classList.add("hidden")
                const headerElement = document.getElementById("index-header-top");
                if (headerElement) {
                    headerElement.classList.remove("hidden");
                }
            }
            // Show camera and filters icons when search is not active
            if (this.hasCameraIconTarget) {
                this.cameraIconTarget.classList.remove("hidden")
            }
            this.filtersIconTargets.forEach(icon => {
                icon.classList.remove("hidden")
            })
            // Show back button when search is not active and input is not focused
            if (this.hasBackButtonTarget && !this.inputFocusedValue) {
                this.backButtonTarget.classList.remove("hidden")
            }
        }

        if (this.hasSearchFormTarget) {
            if (this.toggleValue) {
                this.searchFormTarget.classList.add("z-50")
            } else {
                this.searchFormTarget.classList.remove("z-50")
            }
        }
    }

    inputValueValueChanged() {
        if (this.hasClearButtonTarget) {
            if (this.inputValueValue.length > 0) {
                this.clearButtonTarget.classList.remove("hidden")
            } else {
                this.clearButtonTarget.classList.add("hidden")
            }
        }
    }

    handlePaste(event) {
        const pastedText = (event.clipboardData || window.clipboardData).getData('text/plain').trim();

        if (IngredientDetector.isIngredientList(pastedText)) {
            event.preventDefault();
            IngredientDetector.submitForAnalysis(pastedText);
            this.searchInputTarget.blur();
            this.toggleValue = false;
        }
        // If not an ingredient list, default paste behavior occurs (text is inserted into input)
    }

    checkForIngredientListPaste(text) {
        if (IngredientDetector.isIngredientList(text)) {
            IngredientDetector.submitForAnalysis(text);
            this.searchInputTarget.blur();
            this.toggleValue = false;
        }
    }


    // Image Upload Logic
    triggerImageUpload() {
        this.imageUploadInputTarget.click();
    }

    handleImageSelected(event) {
        const file = event.target.files[0];
        if (file) {
            if (this.hasImageUploadButtonTarget) {
                const loadingButtonController = this.application.getControllerForElementAndIdentifier(this.imageUploadButtonTarget, "loading-button");
                if (loadingButtonController) {
                    loadingButtonController.showLoading();
                }
            }
            this.compressAndSubmitImage(file);
        } else {
            // User cancelled file selection
            if (this.hasImageUploadButtonTarget) {
                this.imageUploadButtonTarget.dispatchEvent(new CustomEvent("loading-button:stop"));
            }
        }
        event.target.value = null; // Reset file input to allow selecting the same file again
    }

    compressAndSubmitImage(file) {
        ImageUploadHelper.compressImage(file, (error, compressedBlob, originalFileName) => {
            if (error) {
                console.error("Image compression failed:", error);
                if (this.hasImageUploadButtonTarget) {
                    this.imageUploadButtonTarget.dispatchEvent(new CustomEvent("loading-button:stop"));
                }
                return;
            }

            this.startDirectUpload(compressedBlob, originalFileName);
        });
    }

    startDirectUpload(imageBlob, originalFileName) {
        ImageUploadHelper.uploadImage(imageBlob, originalFileName, this.directUploadUrlValue, (error, signedId) => {
            if (error) {
                console.error("Direct upload failed:", error);
                if (this.hasImageUploadButtonTarget) {
                    this.imageUploadButtonTarget.dispatchEvent(new CustomEvent("loading-button:stop"));
                }
            } else {
                this.submitAnalysisWithSignedId(signedId);
            }
        });
    }

    submitAnalysisWithSignedId(signedId) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/analysis';
        form.style.display = 'none';

        const csrfToken = ImageUploadHelper.getCsrfToken();
        if (csrfToken) {
            const csrfInput = document.createElement('input');
            csrfInput.type = 'hidden';
            csrfInput.name = ImageUploadHelper.getCsrfParam();
            csrfInput.value = csrfToken;
            form.appendChild(csrfInput);
        } else {
            console.warn("CSRF token not found. Signed ID analysis submission might fail or be insecure.");
        }

        const analysisTypeInput = document.createElement('input');
        analysisTypeInput.type = 'hidden';
        analysisTypeInput.name = 'analysis[analysis_type]';
        analysisTypeInput.value = 'standard';
        form.appendChild(analysisTypeInput);

        const imageSignedIdInput = document.createElement('input');
        imageSignedIdInput.type = 'hidden';
        imageSignedIdInput.name = 'analysis[image]'; // This should match your model's attachment name
        imageSignedIdInput.value = signedId;
        form.appendChild(imageSignedIdInput);

        document.body.appendChild(form);
        form.submit();
        // Hide UI indication for progress if shown
        if (this.hasImageUploadButtonTarget) {
            this.imageUploadButtonTarget.dispatchEvent(new CustomEvent("loading-button:stop"));
        }
        this.searchInputTarget.blur();
        this.toggleValue = false;
        document.body.removeChild(form);
    }

    scrollToTopOnMobile() {
        // Check if we're on mobile (viewport width <= 1024px, matching lg breakpoint)
        const isMobile = window.innerWidth <= 1024;
        // Check if we're NOT in the native app (check for data-turbo-native-app attribute)
        const isNotApp = !document.documentElement.hasAttribute('data-turbo-native-app');

        if (isMobile && isNotApp) {
            // Add a small delay to ensure the focus event has completed
            setTimeout(() => {
                // Scroll the input to the top with some padding (20px from top)
                const elementTop = this.searchInputTarget.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementTop - 40;

                window.scrollTo({
                    top: offsetPosition
                });
            }, 100);
        }
    }

    dismissSearch() {
        this.toggleValue = false
        this.inputValueValue = ""
        this.searchInputTarget.value = ""
        this.searchInputTarget.blur()
    }
}
